import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Smartphone, BatteryCharging, Camera, Plug, Droplets, Wrench, Zap, Clock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import Hero from "@/components/site/Hero";
import PartnersSection from "@/components/site/PartnersSection";
import Iphone18ProLaunch from "@/components/site/Iphone18ProLaunch";
import RepairConfigurator from "@/components/site/RepairConfigurator";
import ReviewsSection from "@/components/site/ReviewsSection";
import StoreShowcase from "@/components/site/StoreShowcase";
import GeraeteretterPraemie from "@/components/site/GeraeteretterPraemie";
import PartnerOffers from "@/components/site/PartnerOffers";
import TariffAndRefurbished from "@/components/site/TariffAndRefurbished";
import ProductCard from "@/components/site/ProductCard";
import { CATEGORIES as FALLBACK_CATEGORIES } from "@/lib/constants";
import { whatsappLinks } from "@/lib/whatsapp";

const SERVICES = [
  { icon: Smartphone, title: "Display Reparatur", desc: "Glasbruch & Touch-Probleme", price: "ab 79€" },
  { icon: BatteryCharging, title: "Akkutausch", desc: "Frische Power für Ihr Handy", price: "ab 59€" },
  { icon: Plug, title: "Ladebuchse", desc: "Schnell wieder einsatzbereit", price: "ab 69€" },
  { icon: Camera, title: "Kamera", desc: "Linse & Modul Reparatur", price: "ab 89€" },
  { icon: Droplets, title: "Wasserschaden", desc: "Professionelle Diagnose", price: "ab 49€" },
  { icon: Wrench, title: "Software & Co.", desc: "Updates, Daten, Setup", price: "ab 39€" },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setFeatured(r.data.slice(0, 4)));
    api.get("/categories").then((r) => { if (r.data?.length) setCategories(r.data); }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#0A0A0B]">
      <Hero />

      {/* iPhone 18 Pro Launch / Anfrage */}
      <Iphone18ProLaunch />

      <PartnersSection />

      {/* Featured Products — Bestseller-Zubehör direkt unter Wertgarantie */}
      <section className="py-20 md:py-24 bg-[#0A0A0B] relative" data-testid="featured-products">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Bestseller</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white mt-3">
                Dein passendes Zubehör.
              </h2>
            </div>
            <Link to="/zubehoer">
              <Button variant="outline" className="rounded-full bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white" data-testid="view-all-products">
                Alle anzeigen <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p) => <ProductCard key={p.sku} p={p} />)}
          </div>

          <div className="mt-16 pt-12 border-t border-white/5">
            <div className="grid grid-cols-3 md:grid-cols-7 gap-3">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/zubehoer?cat=${c.id}`}
                  className="text-center p-4 rounded-2xl bg-[#141417] border border-white/5 text-zinc-300 hover:border-[#DC1928]/40 hover:bg-[#1C1C20] hover:text-white transition-all"
                  data-testid={`cat-${c.id}`}
                >
                  <div className="text-sm font-medium">{c.name}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Premium Store Showcase: Schaufenster, Innenraum, Öffnungszeiten */}
      <StoreShowcase />

      {/* === EXPRESS REPARATUR SECTION === */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="express-section">
        <div className="absolute inset-0 bg-luxury"></div>
        <div className="absolute inset-0 dot-bg opacity-30"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="md:col-span-7"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#DC1928]/10 border border-[#DC1928]/30">
                <Zap className="w-4 h-4 text-[#FF2D40]" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#FF2D40] font-bold">Express Service</span>
              </div>

              <h2 className="font-display text-5xl md:text-7xl font-bold tracking-tight text-white mt-6 leading-[0.98]">
                Display kaputt?
              </h2>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-zinc-400 mt-4 leading-tight">
                Heute bringen.<br/>
                <span className="text-white">In 60 Minuten wieder mitnehmen.</span>
              </h2>

              <p className="mt-7 text-lg text-zinc-400 leading-relaxed max-w-xl">
                Kein Termin nötig. Vorbeikommen, abgeben, kurz Espresso trinken — und schon ist Ihr Handy wieder einsatzbereit. Bei uns kein Schicken, kein Warten, keine versteckten Kosten.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/reparaturen#configurator">
                  <Button className="bg-white text-[#0A0A0B] hover:bg-zinc-100 rounded-full h-12 px-7 btn-lift" data-testid="express-cta">
                    Jetzt anfragen <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
                <a href={whatsappLinks.generic().href} onClick={whatsappLinks.generic().onClick} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="rounded-full h-12 px-7 bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white" data-testid="express-wa">
                    WhatsApp
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="md:col-span-5"
            >
              <ExpressTimerCard />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 md:py-32 bg-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Reparatur-Services</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white mt-3">
                Was wir reparieren.
              </h2>
            </div>
            <div className="md:col-span-7 md:pt-4">
              <p className="text-lg text-zinc-400 leading-relaxed">
                Vom Klassiker bis zum komplexen Schaden — wir kümmern uns um Ihr Smartphone. Mit hochwertigen Ersatzteilen, fairen Preisen und ehrlicher Beratung. Versprochen.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                className="group p-7 rounded-2xl border border-white/5 bg-[#141417] hover:border-[#DC1928]/30 hover:bg-[#1C1C20] transition-all duration-300"
                data-testid={`service-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-white/5 group-hover:bg-[#DC1928] flex items-center justify-center transition-colors">
                  <s.icon className="w-6 h-6 text-zinc-300 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-display text-xl font-semibold text-white mt-5">{s.title}</h3>
                <p className="text-sm text-zinc-500 mt-1.5">{s.desc}</p>
                <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between">
                  <span className="font-display font-semibold text-[#FF2D40] numeric">{s.price}</span>
                  <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-white group-hover:translate-x-1 transition-all" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Configurator */}
      <section className="py-24 md:py-32 bg-[#0F0F12] relative" id="configurator-section">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
        <div className="max-w-5xl mx-auto px-6 lg:px-12 relative">
          <div className="text-center mb-12">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Reparatur Konfigurator</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white mt-3">
              In 3 Klicks zum Angebot.
            </h2>
            <p className="text-lg text-zinc-400 mt-4 max-w-2xl mx-auto">
              Wählen Sie Marke, Modell und Problem. Sie erhalten sofort einen unverbindlichen ab-Preis.
            </p>
          </div>
          <RepairConfigurator />
        </div>
      </section>

      {/* Geräteretter Prämie – Nachhaltigkeit / Reparatur-Bonus */}
      <GeraeteretterPraemie />

      {/* Tarif-Beratung + Refurbished Verkauf */}
      <TariffAndRefurbished />

      {/* === KI-ANKAUFSSTELLE — "Wir kaufen dein Handy!" === */}
      <section className="relative py-24 md:py-28 bg-[#0A0A0B] overflow-hidden" data-testid="buyback-teaser">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
        <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] rounded-full bg-[#DC1928]/8 blur-[120px] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
          <div className="grid md:grid-cols-12 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="md:col-span-6"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs">
                <Sparkles className="w-3.5 h-3.5 text-[#FF2D40]" />
                <span className="text-zinc-200 font-medium">KI-Sofortschätzung</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mt-5 leading-[1.02]">
                Wir kaufen<br/>
                <span className="bg-gradient-to-r from-[#FF2D40] via-[#DC1928] to-[#FF2D40] bg-clip-text text-transparent">dein Handy.</span>
              </h2>
              <p className="text-lg text-zinc-400 mt-5 leading-relaxed max-w-xl">
                Egal ob neu, gebraucht oder defekt — unsere KI schätzt sofort den Ankaufspreis.
                Unverbindliches Angebot innerhalb 24h. Schnell. Fair. Persönlich.
              </p>

              <div className="mt-7 space-y-2.5 max-w-md">
                {[
                  { n: 1, t: "Gerät beschreiben", d: "Marke, Modell, Zustand & Zubehör" },
                  { n: 2, t: "KI-Schätzung erhalten", d: "Sofort eine realistische Preisspanne" },
                  { n: 3, t: "Unverbindliches Angebot", d: "Wir melden uns innerhalb 24h" },
                ].map((s) => (
                  <div key={s.n} className="flex items-start gap-3" data-testid={`buyback-step-teaser-${s.n}`}>
                    <div className="shrink-0 w-7 h-7 rounded-full bg-[#DC1928]/15 border border-[#DC1928]/30 text-[#FF4F5E] text-xs font-bold flex items-center justify-center">{s.n}</div>
                    <div>
                      <div className="text-sm text-white font-medium">{s.t}</div>
                      <div className="text-xs text-zinc-500 leading-tight">{s.d}</div>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/ankauf" className="inline-block mt-8" data-testid="buyback-teaser-cta">
                <Button className="bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-12 px-7 text-base btn-lift glow-red-sm">
                  Jetzt Handy verkaufen <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </motion.div>

            {/* Visual: animated price-card mockup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="md:col-span-6"
            >
              <div className="relative aspect-[5/4] rounded-[2rem] bg-gradient-to-br from-[#1a0509] via-[#141417] to-[#0A0A0B] border border-[#DC1928]/20 overflow-hidden p-8 md:p-10">
                <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#DC1928]/25 blur-[100px] pointer-events-none"></div>
                <div className="relative h-full flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#FF2D40] font-bold">
                      <Sparkles className="w-3 h-3" /> Beispiel KI-Schätzung
                    </div>
                    <div className="mt-3 font-display text-2xl md:text-3xl font-bold text-white leading-tight">
                      iPhone 14<br/>
                      <span className="text-zinc-500 text-lg md:text-xl font-medium">256 GB · sehr gut</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Ankaufspreis</div>
                    <div className="font-display text-5xl md:text-7xl font-bold text-white numeric leading-none mt-2">€180–210</div>
                    <p className="text-xs text-zinc-400 mt-3 leading-relaxed">
                      Beispiel: iPhone 14 256 GB, sehr gut, OVP &amp; Ladekabel dabei.<br/>
                      Unverbindliches Angebot nach Vor-Ort-Check.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <ReviewsSection />

      {/* A1 5G Banner + Schutzfolie Aktionen */}
      <PartnerOffers />

      {/* CTA Strip */}
      <section className="py-20 bg-gradient-to-br from-[#180409] to-[#0A0A0B] relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#DC1928]/20 blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold flex items-center gap-2">
                <Sparkles className="w-3 h-3" /> Ihr Handy bei uns
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white mt-3">
                Wir holen&rsquo;s ab.<br/>
                Wir bringen&rsquo;s heil.
              </h2>
              <p className="text-lg text-zinc-400 mt-4 leading-relaxed">
                Kostenloser Abholservice innerhalb Wiens. Auf Wunsch reparieren wir bei Ihnen vor Ort oder direkt in der Werkstatt.
              </p>
              <div className="mt-8 flex gap-3">
                <Link to="/kontakt">
                  <Button className="bg-white text-[#0A0A0B] hover:bg-zinc-100 rounded-full h-12 px-7 btn-lift" data-testid="cta-contact">
                    Termin vereinbaren <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
                <a href={whatsappLinks.generic().href} onClick={whatsappLinks.generic().onClick} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="rounded-full h-12 px-7 border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-white" data-testid="cta-whatsapp">
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/store/interior-counter-v2.jpeg"
                alt="Haus des Handys Innenraum — Theke mit Zubehörwand und Partnerdisplays"
                className="rounded-3xl aspect-[4/3] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-3xl"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent rounded-3xl pointer-events-none"></div>
              {/* Floating brand badge */}
              <div className="absolute bottom-5 left-5 px-3 py-1.5 rounded-full bg-black/75 backdrop-blur-md border border-white/15 text-[10px] uppercase tracking-[0.22em] text-white font-bold flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF2D40]"></span>
                Im Geschäft
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ExpressTimerCard() {
  const [seconds, setSeconds] = useState(3600); // 60 min visual

  useEffect(() => {
    const t = setInterval(() => setSeconds((s) => s > 0 ? s - 1 : 3600), 1000);
    return () => clearInterval(t);
  }, []);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-[#DC1928]/20 blur-3xl rounded-full"></div>
      <div className="relative bg-gradient-to-br from-[#141417] to-[#0A0A0B] border border-white/10 rounded-[2rem] p-8 grain overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#DC1928]/10 blur-2xl rounded-full"></div>

        <div className="relative flex items-center gap-2 mb-6">
          <div className="w-2.5 h-2.5 rounded-full bg-[#FF2D40] pulse-red"></div>
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Live · Express Slot</span>
        </div>

        <div className="font-display font-bold text-[88px] leading-none text-white numeric tracking-tighter">
          {mm}<span className="text-[#DC1928]">:</span>{ss}
        </div>
        <div className="text-sm text-zinc-500 mt-2">Minuten · Sekunden bis fertig</div>

        <div className="mt-8 space-y-3">
          <Step n="1" text="Vorbeikommen" done />
          <Step n="2" text="Diagnose & Reparatur" active />
          <Step n="3" text="Abholen — wie neu" />
        </div>

        <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <Clock className="w-3.5 h-3.5" /> Ø Reparaturzeit
          </div>
          <div className="font-display font-bold text-white">60 Minuten</div>
        </div>
      </div>
    </div>
  );
}

function Step({ n, text, done, active }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold ${
        done ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
          : active ? "bg-[#DC1928] text-white"
          : "bg-white/5 text-zinc-500 border border-white/10"
      }`}>
        {done ? "✓" : n}
      </div>
      <span className={`text-sm ${active ? "text-white font-medium" : "text-zinc-400"}`}>{text}</span>
    </div>
  );
}
